import React from "react";
import {
  Input,
  Heading,
  Button,
  Checkbox,
  Text,
  InputForm,
  MultiSelect,
  Radio,
  Option
} from "src/components";
import { AuthStore, RoutingStore, SpaceStore } from "src/stores";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import Box from "react-raster";
import { theme } from "src/config";
import { User } from "src/models";
import {
  SpaceType,
  UserRole,
  ISpaceUser,
  IGroupUser,
  UserType,
  UserStatus
} from "src/types";
import { Page } from "src/routes";
import toast from "react-hot-toast";
import { runInAction } from "mobx";

export interface IUserInviteFormProps {
  authStore?:    AuthStore;
  routingStore?: RoutingStore;
  spaceStore?:   SpaceStore;
  spaceType:     SpaceType;
  space_id:      number;
}

export const UserInviteForm = inject("authStore", "routingStore", "spaceStore")(observer((props: IUserInviteFormProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const { error_message } = props.authStore;
    const [email,     setEmail]     = React.useState<string>("");
    const [role,      setRole]      = React.useState<Option>({label: t("member"), value: "member"});
    const [spaces,    setSpaces]    = React.useState<any[]>([]);
    const [groups,    setGroups]    = React.useState<any[]>([]);
    const [initials,  setInitials]  = React.useState<string>("");
    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName,  setLastName]  = React.useState<string>("");
    const [userTitle, setUserTitle] = React.useState<string>("");
    const [hideOnBoards, setHideOnBoardsChecked] = React.useState(false);
    const [hideInLists,  setHideInListsChecked]  = React.useState(false);
    const [companyName,  setCompanyName] = React.useState<string>("");
    const [phoneNumber,  setPhoneNumber] = React.useState<string>("");
    const [accountType,  setAccountType] = React.useState<UserType>(UserType.Default);

    const workspaceId = props.authStore.getCurWorkspaceID;
    const spaceId     = props.space_id;

    React.useEffect(() => {
      runInAction(() => props.authStore.error_message = null);
      if (!spaceId) {
        props.routingStore.push(`/${Page.WorkSpaceSettings}/${workspaceId}`);
      }
    }, []);

    const onSubmit = async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      if (!spaceId) {
        console.error("missing space id");
      } else {
        props.routingStore.goBack();
      };

      const user = await User.create({
        email:        UserType.Accountless === accountType ? "": (email || '').toLowerCase(),
        company_name: companyName,
        type:         accountType,
        phone:        phoneNumber,
        first_name:   firstName,
        last_name:    lastName,
        workspace_id: workspaceId,
        userGroups:   [],
        groupUsers:   [],
      });

      const currentSpaceUser: ISpaceUser = {
        user_id:        user.id,
        space_id:       spaceId,
        role:           role.value as UserRole,
        workspace_id:   workspaceId,
        hide_in_list:   hideInLists,
        hide_in_matrix: hideOnBoards,
        space_type:     props.spaceType,
        status: 
          UserType.Accountless === accountType
            ? UserStatus.Created
            : UserStatus.Invited,
        mentions:      true,
        daily_summary: [UserRole.Owner, UserRole.Leader].includes(role.value),
        watching:      role.value === UserRole.Member,
        participation: true,
      };

      const spacesToInvite: ISpaceUser[] = [currentSpaceUser];
      spaces.map((space) => {
        spacesToInvite.push(
          Object.assign({}, currentSpaceUser, { space_id: space.id })
        );
      });

      const groupsToAdd: IGroupUser[] = [];
      groups.map((g) => {
        groupsToAdd.push({
          group_id:     g.id,
          user_id:      user.id,
          workspace_id: workspaceId
        });
      });

      // Invite the user;
      const result = await props.authStore.inviteUser(
        user,
        spacesToInvite,
        groupsToAdd
      );

      if (result) {
        setEmail("");
        setHideOnBoardsChecked(false);
        setHideInListsChecked(false);
        setRole(roles.find((r) => r.value === "member"));
        setSpaces([]);
        setGroups([]);
        setInitials("");
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setUserTitle("");
        setCompanyName("");
        const m =
          accountType === UserType.Default
            ? t("is_user_successfully_invited")
            : t("is_user_successfully_created");
        toast.success(`${user.first_name} ${m}`);
      } else {
        toast.error(t("failed_invite_user"));
      }
    };

    React.useEffect(() => {
      if (accountType == UserType.Accountless) {
        setRole(roles.find((r) => r.value === "member"));
      }
    }, [accountType]);

    // Available roles to select for the new user
    const roles = (() => {
      const st = props.spaceType;
      const roles: Option[] = [];

      if (st === SpaceType.System) {
        roles.push(...[{ label: t("admin"), value: "admin" }]);
      }
      if (st === SpaceType.Workspace) {
        roles.push(...[{ label: t("workspace_owner"), value: "owner" }]);
      }

      roles.push(
        ...[
          { label: t("project_leader"), value: "leader" },
          { label: t("project_viewer"), value: "viewer" },
          { label: t("member"), value: "member" }
        ]
      );

      return roles;
    })();

    // Available groups to select for the new user
    // const availableGroups = useMemo(() => {
    //   const items = props.groupStore.getItems;
    //   return (items || []).map((item) => {
    //     return {
    //       id: item.id,
    //       name: item.name
    //       // group: item.group
    //     };
    //   });
    // }, [props.groupStore.getItems]);

    // const availableSpaces = useMemo(() => {
    //   const type =
    //     props.spaceType == SpaceType.Project
    //       ? SpaceType.Room
    //       : SpaceType.Project;
    //   const items = props.spaceStore.getItemsByType(type);
    //   return (items || []).map((item) => {
    //     return {
    //       id: item.id,
    //       name: item.title
    //     };
    //   });
    // }, [props.spaceStore.getItems]);

    let valid = true;
    if (accountType === UserType.Default) {
      valid = !!email && !!role;
    }

    return (
      <Box gridRowGap={theme.layout.gutter}>
        <Heading type="h2">{t(`add_new_${props.spaceType}_member`)}</Heading>
        <Box>
          <hr />
        </Box>
        {[
          {
            type: UserType.Default,
            name: "real_user_account",
            info: t("default_account_type")
            /* Ett konto som går att logga in med i detta system */
          },
          {
            type: UserType.Accountless,
            name: "accountless_user_account",
            info: t("accountless_account_type")
            /* "Användaren kan inte logga in och kan bara medverka lokalt på möten" */
          }
        ].map((option) => (
          <Box key={option.type} style={{ gridGap: 0 }}>
            <Radio
              key      = {option.type}
              checked  = {accountType === option.type}
              disabled = {false}
              value    = {t(option.name)}
              onClick  = {() => setAccountType(option.type)}
            />
            <Text size="small" color={theme.font.color.slate11} style = {{ margin: "0.5rem 0rem" }}>{t(option.info)}</Text>
          </Box>
        ))}

        <InputForm gap = '1.5rem'>
          {accountType === UserType.Default && (
            <Input
              key          = "email"
              type         = "email"
              required     = {true}
              label        = {t("email")}
              value        = {email}
              onChange     = {(text) => setEmail(text)}
              errorMessage = {t("validation_error", { what: t("email") })}
              showError    = {!!error_message && !email}
            />
          )}
          <Input
            key      = "company_name"
            type     = "text"
            label    = {t("company_name")}
            value    = {companyName}
            onChange = {(text) => setCompanyName(text)}
          />
          <Input
            key      = "first_name"
            type     = "text"
            label    = {t("first_name")}
            value    = {firstName}
            onChange = {(text) => setFirstName(text)}
          />
          <Input
            key      = "last_name"
            type     = "text"
            label    = {t("last_name")}
            value    = {lastName}
            onChange = {(text) => setLastName(text)}
          />
          <Input
            key      = "phone_number"
            type     = "text"
            label    = {t("phone_number")}
            value    = {phoneNumber}
            onChange = {(text) => setPhoneNumber(text)}
          />
          <Input
            key       = "title"
            type      = "text"
            label     = {t("title")}
            // maxLength = {2}
            value     = {userTitle}
            onChange  = {(text) => setUserTitle(text)}
          />
          <Input
            key       = "user_initials"
            type      = "text"
            label     = {t("user_initials")}
            value     = {initials}
            maxLength = {Number("3")}
            onChange  = {(text) => setInitials(text)}
          />

          {accountType === UserType.Default && (
            <MultiSelect
              singleSelect
              forceSelect
              disableSearch
              disableAutoFocus
              key          = "role"
              placeholder  = {t("role")}
              options      = {roles}
              selected     = {role ? [role] : []}
              onChange     = {(selected: Option) => setRole(selected)}
            />
          )}
          <Box />
          <Checkbox
            key      = "hide_in_matrix_option"
            checked  = {hideOnBoards}
            disabled = {false}
            size     = "small"
            label    = {t("hide_in_matrix")}
            onClick  = {() => setHideOnBoardsChecked(!hideOnBoards)}
          />
          <Checkbox
            key      = "hide_in_lists_option"
            checked  = {hideInLists}
            disabled = {false}
            size     = "small"
            label    = {t("hide_in_lists")}
            onClick  = {() => setHideInListsChecked(!hideInLists)}
          />
        </InputForm>
        <Box
          cols           = {1}
          display        = "flex"
          justifyContent = "flex-end"
          flexDirection  = "column"
        >
          <Button
            disabled = {!valid}
            variant  = "primary"
            size     = "large"
            onClick  = {onSubmit}
          >
            {accountType === UserType.Default
              ? t("invite_user")
              : t("create_user")}
          </Button>
        </Box>
      </Box>
    );
  })
);
